import { useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';

interface OnboardingCheckProps {
  children: React.ReactNode;
}

/**
 * OnboardingCheck redirects users to organization onboarding if they don't have an organization.
 * This ensures all users have an organization context before accessing the app.
 * If invite_token is present in URL, preserves it through onboarding flow.
 */
export function OnboardingCheck({ children }: OnboardingCheckProps) {
  const { user, loading: authLoading } = useCognitoAuth();
  const { organizations, loading: orgLoading, error: orgError, refreshOrganizations } = useOrganization();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite_token');

  const orgCount = organizations?.length ?? 0;

  const handleRetry = useCallback(() => {
    void refreshOrganizations();
  }, [refreshOrganizations]);

  // Only block loading spinner if auth is still resolving OR if we genuinely have zero orgs
  if (authLoading || (orgLoading && orgCount === 0)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🌿</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user, ProtectedRoute handles redirect to login
  if (!user) {
    return <>{children}</>;
  }

  // Don't redirect on fetch errors — the user may have orgs but the request failed
  if (orgCount === 0 && orgError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">Couldn't load your organizations</h2>
          <p className="text-muted-foreground mb-4">{orgError}</p>
          <button
            onClick={handleRetry}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // If user has no organizations, redirect to onboarding (preserve invite_token)
  if (orgCount === 0) {
    const target = inviteToken
      ? `/onboarding/organization?invite_token=${inviteToken}`
      : '/onboarding/organization';
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
